import { getRegionConfig } from "@/lib/regionConfig";

const regionConfig = getRegionConfig();

export interface DeliveryAvailability {
  available: boolean;
  after_hours?: boolean;
  after_hours_surcharge?: string;
  terms_and_conditions?: string;
  service_start_time?: string;
  service_end_time?: string;
  min_lead_days?: number;
  min_rental_days?: number;
  rental_duration_minutes?: number;
  minimum_return_at?: string;
  error_code?: string;
  message?: string;
}

export interface DeliveryPrice extends DeliveryAvailability {
  distance_km?: string;
  delivery_fee?: string;
  total_fee?: string;
  max_radius_km?: string;
}

export interface DeliveryStation {
  id: number;
  rw_station_id: string;
  rw_name: string;
  rw_address1?: string | null;
  rw_address2?: string | null;
  rw_city?: string | null;
  rw_postal_code?: string | null;
  rw_latitude?: string | null;
  rw_longitude?: string | null;
}

export interface DeliveryOrder {
  id: number;
  reservation_id: number;
  station_id: number;
  driver_id?: number | null;
  type: "pickup" | "return";
  status: string;
  vehicle_plate?: string | null;
  vehicle_model?: string | null;
  customer_address: string;
  customer_lat: string;
  customer_lng: string;
  distance_km: string;
  delivery_fee: string;
  after_hours_surcharge: string;
  waiting_fee: string;
  scheduled_at: string;
  arrived_at?: string | null;
  completed_at?: string | null;
  waiting_started_at?: string | null;
  waiting_ended_at?: string | null;
  waiting_seconds?: number | null;
  arrival_photo_url?: string | null;
  handover_photo_url?: string | null;
  no_show_photo_url?: string | null;
  customer_display_name?: string | null;
  waiting_fee_status: string;
  station?: DeliveryStation | null;
}

export interface DeliverySelection {
  type: "pickup" | "return";
  customer_address: string;
  customer_remark?: string;
  customer_lat: number;
  customer_lng: number;
  scheduled_at: string;
  price?: DeliveryPrice;
}

async function parseResponse<T>(response: Response): Promise<T> {
  const json = await response.json();

  if (!response.ok) {
    throw json;
  }

  return json;
}

export async function checkDeliveryAvailability(params: {
  station_id: string;
  type: "pickup" | "return";
  scheduled_at: string;
  rental_start_at: string;
  rental_end_at: string;
  apiUrl?: string;
}): Promise<DeliveryAvailability> {
  const apiUrl = params.apiUrl || regionConfig.apiUrl;
  const query = new URLSearchParams({
    station_id: params.station_id,
    type: params.type,
    scheduled_at: params.scheduled_at,
    rental_start_at: params.rental_start_at,
    rental_end_at: params.rental_end_at,
  });

  const response = await fetch(`${apiUrl}delivery/check-availability?${query.toString()}`);

  return parseResponse(response);
}

export async function calculateDeliveryPrice(params: {
  station_id: string;
  type: "pickup" | "return";
  scheduled_at: string;
  rental_start_at: string;
  rental_end_at: string;
  customer_lat: number;
  customer_lng: number;
  apiUrl?: string;
}): Promise<DeliveryPrice> {
  const apiUrl = params.apiUrl || regionConfig.apiUrl;
  const response = await fetch(`${apiUrl}delivery/calculate-price`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(params),
  });

  return parseResponse(response);
}

export async function addDeliveryToOrder(params: DeliverySelection & {
  reservation_id: string;
  apiUrl?: string;
}): Promise<{ message: string; order: DeliveryOrder }> {
  const apiUrl = params.apiUrl || regionConfig.apiUrl;
  const response = await fetch(`${apiUrl}delivery/add-to-order`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(params),
  });

  return parseResponse(response);
}
